import React, { useState } from 'react';
import { ArrowRight, Star } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { useToast } from '../hooks/use-toast';
import { countryCodes } from '../data/mockData';

const HeroBanner = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '', email: '', countryCode: '+1', phone: '', description: ''
  });
  const [errors, setErrors] = useState({ email: '', phone: '' });

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleEmailChange = (value) => {
    setFormData({ ...formData, email: value });
    if (value && !validateEmail(value)) {
      setErrors({ ...errors, email: 'Please enter a valid email' });
    } else {
      setErrors({ ...errors, email: '' });
    }
  };

  const handlePhoneChange = (value) => {
    const digitsOnly = value.replace(/\D/g, '');
    if (digitsOnly.length <= 10) {
      setFormData({ ...formData, phone: digitsOnly });
      if (digitsOnly && digitsOnly.length !== 10) {
        setErrors({ ...errors, phone: 'Must be 10 digits' });
      } else {
        setErrors({ ...errors, phone: '' });
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    let hasErrors = false;
    const newErrors = { email: '', phone: '' };
    if (!validateEmail(formData.email)) { newErrors.email = 'Please enter a valid email'; hasErrors = true; }
    if (formData.phone.length !== 10) { newErrors.phone = 'Must be 10 digits'; hasErrors = true; }
    if (hasErrors) { setErrors(newErrors); return; }
    toast({ title: "Request Received!", description: "Our trademark experts will contact you within 24 hours." });
    setFormData({ name: '', email: '', countryCode: '+1', phone: '', description: '' });
    setErrors({ email: '', phone: '' });
  };

  return (
    <section id="home" data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden" style={{
      background: 'linear-gradient(135deg, #1a0a2e 0%, #2d1052 40%, #4C1D95 70%, #3b1278 100%)'
    }}>
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-[#7C3AED]/20 blur-[120px]" />
        <div className="absolute -bottom-60 -left-40 w-[500px] h-[500px] rounded-full bg-[#14B8A6]/15 blur-[100px]" />
        <div className="absolute top-1/4 left-1/3 w-[300px] h-[300px] rounded-full bg-[#7C3AED]/10 blur-[80px]" />
      </div>

      <div className="mx-auto w-full max-w-7xl px-4 md:px-8 relative z-10 pt-28 pb-20">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-7 space-y-7">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/10 px-4 py-2 rounded-full">
              <div className="flex -space-x-1">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-[#14B8A6] text-[#14B8A6]" />)}
              </div>
              <span className="text-xs font-medium text-white/80">Trusted by 10,000+ Brands</span>
            </div>

            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[1.05] text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Protect Your Brand.
              <br />
              <span className="bg-gradient-to-r from-[#14B8A6] to-[#5DE4D4] bg-clip-text text-transparent">
                Own Your Future.
              </span>
            </h1>

            <p className="text-lg text-white/60 leading-relaxed max-w-xl">
              Expert USPTO trademark filing and federal brand protection. Licensed trademark attorneys handle your complete trademark registration, from initial search to final certification, starting at just <span className="text-[#14B8A6] font-semibold">$49</span>.
            </p>

            <div className="flex flex-col gap-4 max-w-lg">
              {[
                { title: 'Federal Protection in All 50 States', desc: 'Secure exclusive legal rights to your brand name, logo, or slogan nationwide with a USPTO trademark registration.' },
                { title: 'Licensed Trademark Attorneys', desc: 'Every application is prepared and filed by experienced attorneys who understand USPTO requirements and IP law.' },
                { title: 'Fast 48-Hour Filing Guarantee', desc: 'We prepare and submit your trademark application within 48 hours of receiving your complete details.' },
              ].map((point, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-lg bg-[#14B8A6]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-sm font-black text-[#14B8A6]">{i + 1}</span>
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-white mb-0.5">{point.title}</h3>
                    <p className="text-xs text-white/40 leading-relaxed">{point.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div data-testid="hero-contact-form" className="bg-white rounded-2xl p-7 shadow-2xl shadow-black/20 relative">
              <div className="absolute -top-3 left-6">
                <span className="bg-gradient-to-r from-[#7C3AED] to-[#14B8A6] text-white px-4 py-1.5 rounded-full text-xs font-bold tracking-wide">
                  STARTING AT $49
                </span>
              </div>
              <div className="mb-5 mt-2">
                <h3 className="text-xl font-bold text-zinc-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  Register Your Trademark
                </h3>
                <p className="text-sm text-zinc-500 mt-1">File with the USPTO and protect your brand</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-3">
                <div>
                  <Label htmlFor="hero-name" className="text-zinc-600 text-xs font-medium">Full Name *</Label>
                  <Input id="hero-name" data-testid="hero-name-input" placeholder="Enter your full name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className="h-10 bg-zinc-50 border-zinc-200 text-zinc-900 placeholder:text-zinc-400 focus:border-[#7C3AED] focus:ring-1 focus:ring-[#7C3AED] rounded-lg mt-1" />
                </div>

                <div>
                  <Label htmlFor="hero-email" className="text-zinc-600 text-xs font-medium">Email *</Label>
                  <Input id="hero-email" data-testid="hero-email-input" type="email" placeholder="your@email.com" value={formData.email} onChange={(e) => handleEmailChange(e.target.value)} required className={`h-10 bg-zinc-50 text-zinc-900 placeholder:text-zinc-400 rounded-lg mt-1 ${errors.email ? 'border-red-500' : 'border-zinc-200'} focus:border-[#7C3AED] focus:ring-1 focus:ring-[#7C3AED]`} />
                  {errors.email && <p className="text-red-500 text-xs mt-0.5">{errors.email}</p>}
                </div>

                <div>
                  <Label htmlFor="hero-phone" className="text-zinc-600 text-xs font-medium">Phone *</Label>
                  <div className="flex gap-2 mt-1">
                    <Select value={formData.countryCode} onValueChange={(v) => setFormData({ ...formData, countryCode: v })}>
                      <SelectTrigger data-testid="hero-country-select" className="w-[100px] h-10 bg-zinc-50 border-zinc-200 text-zinc-900 rounded-lg">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px] bg-white border-zinc-200">
                        {countryCodes.map((item) => (
                          <SelectItem key={item.code} value={item.code} className="text-zinc-900">
                            <span className="flex items-center gap-2"><span>{item.flag}</span><span>{item.code}</span></span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input id="hero-phone" data-testid="hero-phone-input" type="tel" placeholder="1234567890" value={formData.phone} onChange={(e) => handlePhoneChange(e.target.value)} required maxLength={10} className={`flex-1 h-10 bg-zinc-50 text-zinc-900 placeholder:text-zinc-400 rounded-lg ${errors.phone ? 'border-red-500' : 'border-zinc-200'} focus:border-[#7C3AED] focus:ring-1 focus:ring-[#7C3AED]`} />
                  </div>
                  {errors.phone && <p className="text-red-500 text-xs mt-0.5">{errors.phone}</p>}
                </div>

                <div>
                  <Label htmlFor="hero-desc" className="text-zinc-600 text-xs font-medium">What do you need?</Label>
                  <Textarea id="hero-desc" data-testid="hero-description-input" placeholder="e.g. Register my brand name and logo with the USPTO" rows={2} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="bg-zinc-50 border-zinc-200 text-zinc-900 placeholder:text-zinc-400 focus:border-[#7C3AED] focus:ring-1 focus:ring-[#7C3AED] resize-none rounded-lg mt-1" />
                </div>

                <Button type="submit" data-testid="hero-contact-submit" className="w-full bg-gradient-to-r from-[#7C3AED] to-[#6D28D9] hover:from-[#6D28D9] hover:to-[#5B21B6] text-white h-11 text-sm font-semibold rounded-lg active:scale-[0.97] shadow-lg shadow-purple-500/25">
                  Get My Free Consultation <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
                <p className="text-[10px] text-zinc-400 text-center">By submitting, you agree to our Terms and Privacy Policy.</p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroBanner;
